import React, { useState } from 'react'

import { useForm } from 'react-hook-form'

import { Icon, Modal, StandardButton, Txt } from 'src/components/atoms'
import { SquareIconButton } from 'src/components/atoms/inputs/SquareIconButton'
import { AssignMailCheckbox, ProjectAssignInputArea } from 'src/components/molecules'
import { upsertProjectSchema } from 'src/components/organisms'

import { yupResolver } from 'src/customYup'
import {
  useGA,
  useGetProfile,
  useGetProject,
  useGetProjects,
  useProject,
  useToast,
} from 'src/hooks'
import { ProfileEntity, ProjectDetailEntity, UpsertProjectFormEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  isOpen: boolean
  profile: ProfileEntity
  compassRoles: CompassRoleEntity[]
  project: ProjectDetailEntity
  defaultSelectedTab?: 'internal' | 'external'
  onClose: () => void
}

export function EditCompassAssignModal({
  isOpen,
  profile,
  compassRoles,
  project,
  defaultSelectedTab,
  onClose,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)

  const { refetch: refetchProfile } = useGetProfile()
  const { refetch: refetchProjects } = useGetProjects()
  const { refetch: refetchProjectDetail } = useGetProject({ projectId: project.id })

  const { updateProject, toUpsertFormEntity } = useProject()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { sendEvent } = useGA()

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<UpsertProjectFormEntity>({
    resolver: yupResolver(upsertProjectSchema),
    defaultValues: toUpsertFormEntity({ entity: project }),
  })

  const onSubmit = async (formEntity: UpsertProjectFormEntity) => {
    setIsLoading(true)

    try {
      const { statusName } = formEntity
      if (!statusName) {
        throw new Error('ステータスを選択してください')
      }

      await updateProject({ projectId: project.id, formEntity })
      await Promise.all([refetchProjects(), refetchProjectDetail(), refetchProfile()])
      displayStandardTextToast('更新しました')
      sendEvent('update_status', {
        project_id: project.id,
        status: statusName,
      })

      onClose()
    } catch {
      displayErrorTextToast('更新に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} canCloseClickOuter={false} maxWidth={false}>
      <div className="tw-w-[68.57rem] tw-h-[calc(100vh-5.8rem)]">
        <form onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-h-full">
          <div className="tw-border-b tw-border-b-border-secondary tw-py-s0 tw-shrink-0">
            <div className="tw-px-s2 tw-w-full">
              <div className="tw-full-justify">
                <Txt sizeType="l" className="tw-font-bold tw-text-standardText-primary">
                  ROOV compassアサイン
                </Txt>

                <SquareIconButton type="button" size="s" onClick={onClose}>
                  <Icon path="close/soft/line" size="--r2" />
                </SquareIconButton>
              </div>
            </div>
          </div>

          <ProjectAssignInputArea
            compassRoles={compassRoles}
            profile={profile}
            project={project}
            setValue={setValue}
            watch={watch}
            formErrors={errors}
            trigger={trigger}
            defaultSelectedTab={defaultSelectedTab}
            className="tw-shrink tw-overflow-auto tw-h-full"
          />

          <div className="tw-border-t tw-border-t-border-secondary tw-py-s0 tw-px-s2 tw-full-justify  tw-shrink-0">
            <AssignMailCheckbox
              isSendAssignEmail={watch('isSendAssignEmail')}
              updateCheck={updateValue => setValue('isSendAssignEmail', updateValue)}
            />
            <div className="tw-inline-flex">
              <StandardButton
                type="button"
                buttonType="tertiary"
                text="キャンセル"
                onClick={onClose}
                disabled={isLoading}
              />
              <StandardButton
                type="submit"
                text={isLoading ? undefined : '保存'}
                appendIconPath={isLoading ? 'check/soft/line' : undefined}
                isLoading={isLoading}
                className="tw-ml-s0"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}
